import React, { useState, useRef } from 'react';
import {
  Button,
  Card,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Backdrop,
  CircularProgress
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useLocalization } from 'src/context/LocalizationContext';
import { HotTable } from '@handsontable/react';
import Handsontable from 'handsontable';
import 'handsontable/dist/handsontable.full.css';
import * as XLSX from 'xlsx';
import Services from 'src/services/Services';

export default function RequestManagementPage() {
  const { language } = useLocalization();
  const parsedUser = JSON.parse(localStorage.getItem('user'));
  const [loading, setLoading] = useState(false);

  // Statü seçenekleri
  const statusOptions = ['Satış Tamamlandı', 'Satış İptal', 'Satış İptal Eski Üye'];

  // Tablo başlangıç verisi (boş veri yapısı)
  const [tableData, setTableData] = useState([
    {
      applicationCode: '',
      applicantName: '',
      phone: '',
      brandName: '',
      campaignName: '',
      branchCode: '',
      pointName: '',
      creationDate: '',
      status: ''
    }
  ]);

  // Güncelleme sonucunu (başarılı ve hatalı satırlar) tutan state
  const [updateResult, setUpdateResult] = useState({ success: [], errors: [] });
  const [modalOpen, setModalOpen] = useState(false);

  // Excel importu için file input referansı
  const fileInputRef = useRef(null);

  // Kolon sıralaması (row number renderersiz)
  const columnOrder = [
    'applicationCode',
    'applicantName',
    'phone',
    'brandName',
    'campaignName',
    'branchCode',
    'pointName',
    'creationDate',
    'status'
  ];

  function rowNumberRenderer(instance, td, row, col, prop, value, cellProperties) {
    Handsontable.renderers.TextRenderer.apply(this, arguments);
    td.innerHTML = row + 1;
    return td;
  }

  const columns = [
    {
      data: null,
      readOnly: true,
      renderer: rowNumberRenderer,
      width: 25
    },
    {
      data: 'applicationCode',
      className: 'htCenter htMiddle',
      type: 'text'
    },
    {
      data: 'applicantName',
      className: 'htCenter htMiddle',
      type: 'text'
    },
    {
      data: 'phone',
      className: 'htCenter htMiddle',
      type: 'text'
    },
    {
      data: 'brandName',
      className: 'htCenter htMiddle',
      type: 'text'
    },
    {
      data: 'campaignName',
      className: 'htCenter htMiddle',
      type: 'text'
    },
    {
      data: 'branchCode',
      className: 'htCenter htMiddle',
      type: 'text'
    },
    {
      data: 'pointName',
      className: 'htCenter htMiddle',
      type: 'text'
    },
    {
      data: 'creationDate',
      type: 'date',
      className: 'htCenter htMiddle',
      dateFormat: 'DD/MM/YYYY',
      correctFormat: true,
      placeholder: 'GG/AA/YYYY',
      datePickerConfig: {
        firstDay: 1,
        showWeekNumber: true,
        i18n: {
          previousMonth: 'Previous Month',
          nextMonth: 'Next Month',
          months: [
            'Ocak',
            'Şubat',
            'Mart',
            'Nisan',
            'Mayıs',
            'Haziran',
            'Temmuz',
            'Ağustos',
            'Eylül',
            'Ekim',
            'Kasım',
            'Aralık'
          ],
          weekdays: [
            'Pazar',
            'Pazartesi',
            'Salı',
            'Çarşamba',
            'Perşembe',
            'Cuma',
            'Cumartesi'
          ],
          weekdaysShort: ['Pa', 'Pzt', 'Salı', 'Ça', 'Pe', 'Cu', 'Ct']
        }
      }
    },
    {
      data: 'status',
      type: 'autocomplete',
      className: 'htCenter htMiddle',
      source: statusOptions,
      strict: true,
      allowInvalid: false
    }
  ];

  const colHeaders = [
    'No',
    'Başvuru Kodu',
    'Başvuran Kişi',
    'Telefon',
    'Marka Adı',
    'Kampanya Adı',
    'Şube Kodu',
    'Nokta Adı',
    'Oluşturma Tarihi',
    'Durum'
  ];

  // Excel import düğmesine tıklandığında gizli file input açılır.
  const handleExcelImportClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Dosya seçildiğinde, Excel dosyasını okuyup tabloya aktarır.
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const binaryStr = e.target.result;
      const workbook = XLSX.read(binaryStr, { type: 'binary' });
      // İlk sayfayı alıyoruz
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      // Sayfayı JSON formatına çeviriyoruz; defval: '' boş hücreler için boş string verir.
      const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: '' });
      console.log('Excel verisi:', jsonData);
      // Excel verisini, tablo veri yapısına eşleyecek şekilde mapleyin:
      const mappedData = jsonData.map((row) => ({
        applicationCode: row["Başvuru Kodu"],
        applicantName: row["Başvuran Kişi"],
        phone: row["Telefon"],
        brandName: row["Marka Adı"],
        campaignName: row["Kampanya Adı"],
        branchCode: row["Şube Kodu"],
        pointName: row["Nokta Adı"],
        creationDate: row["Oluşturulma Tarihi"],
        status: statusOptions.includes(row["Durum"]) ? row["Durum"] : ""
      }));
      setTableData(mappedData);
    };
    reader.readAsBinaryString(file);
    event.target.value = null;
  };

  const handleSave = () => {
    setLoading(true);
    const filledRows = tableData.filter((row) =>
      Object.values(row).some(
        (val) => (val != null ? val.toString().trim() !== '' : false)
      )
    );
  
    const requiredFields = ['applicationCode', 'status'];
  
    const completeRows = filledRows.filter((row) =>
      requiredFields.every(
        (field) => row[field] != null && row[field].toString().trim() !== ''
      )
    );
  
    const incompleteRows = filledRows
      .filter((row) =>
        requiredFields.some(
          (field) => row[field] == null || row[field].toString().trim() === ''
        )
      )
      .map((row) => {
        const missingFields = [];
        if (
          row.applicationCode == null ||
          row.applicationCode.toString().trim() === ''
        ) {
          missingFields.push('Başvuru Kodu');
        }
        if (row.status == null || row.status.toString().trim() === '') {
          missingFields.push('Durum bilgisi');
        }
        return { ...row, errorReason: missingFields.join(' ve ') + ' eksik' };
      });
  
    const statusMapping = {
      'Satış Tamamlandı': 1, // Onaylandı
      'Satış İptal': 2,
      'Satış İptal Eski Üye': 4
    };
  
    const payload = completeRows.map((row) => ({
      campaignApplyCode: row.applicationCode,
      status: statusMapping[row.status]
    }));
  
    Services.BolParaMassUpdateStatus(parsedUser.token, payload)
      .then((res) => {
        if (res.data.status) {
          const savedRows = completeRows;
          const result = { success: savedRows, errors: incompleteRows };
          setUpdateResult(result);
          const emptyRows = tableData.filter(
            (row) =>
              !Object.values(row).some(
                (val) => (val != null ? val.toString().trim() !== '' : false)
              )
          );
          setTableData([...incompleteRows, ...emptyRows]);
          setModalOpen(true);
        } else {
          // Yeni hata mesajı formatı: {"notUpdated":["..."],"notFound":["..."]}
          let errorObj = {};
          try {
            errorObj = JSON.parse(res.data.message);
          } catch (e) {
            console.error('Hata mesajı parse edilirken sorun:', e);
          }
          const notUpdatedCodes = errorObj.notUpdated || [];
          const notFoundCodes = errorObj.notFound || [];
  
          const notUpdatedErrors = completeRows
            .filter((row) => notUpdatedCodes.includes(row.applicationCode))
            .map((row) => ({
              ...row,
              errorReason: `Statüsü Onay Bekleyen olmadığı için güncellenemeyen başvuru`
            }));
  
          const notFoundErrors = completeRows
            .filter((row) => notFoundCodes.includes(row.applicationCode))
            .map((row) => ({
              ...row,
              errorReason: `Başvuru Kodu Bulunamadı`
            }));
  
          const savedRows = completeRows.filter(
            (row) =>
              !notUpdatedCodes.includes(row.applicationCode) &&
              !notFoundCodes.includes(row.applicationCode)
          );
  
          const result = {
            success: savedRows,
            errors: incompleteRows.concat(notUpdatedErrors, notFoundErrors)
          };
  
          setUpdateResult(result);
          const emptyRows = tableData.filter(
            (row) =>
              !Object.values(row).some(
                (val) => (val != null ? val.toString().trim() !== '' : false)
              )
          );
          setTableData([...result.errors, ...emptyRows]);
          setModalOpen(true);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.log('Statü güncelleme hatası:', error);
        setLoading(false);
      });
  };
  

  return (
    <>
      <Helmet>
        <title>{language.bol_para_request_management.titles.site_title}</title>
      </Helmet>
      <Stack style={{ paddingInline: '25px' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            {language.bol_para_request_management.titles.title}
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              sx={{
                width: 142,
                height: 40,
                backgroundColor: '#088AB2',
                fontSize: 14,
                fontFamily: 'Inter',
                fontWeight: 500,
                borderRadius: '5px',
                padding: '8px 16px 8px 16px',
                boxShadow: '0px 6px 12px rgba(8, 138, 178, 0.5)',
                gap: '6px',
                '&:hover': {
                  backgroundColor: '#067294'
                }
              }}
              variant="contained"
              onClick={handleExcelImportClick}
            >
              Excel İçe Aktar
            </Button>
            <Button
              sx={{
                boxShadow: 'none',
                backgroundColor: '#DC423C',
                fontWeight: 500,
                fontSize: 13,
                fontFamily: 'Inter',
                height: 40,
                borderRadius: '5px',
                '&:hover': { backgroundColor: '#B53228' },
                padding: '8px 16px'
              }}
              variant="contained"
              onClick={handleSave}
            >
              Toplu Statü Güncelle
            </Button>
          </Stack>
        </Stack>
        {loading ? (
          <Backdrop
            sx={{ color: '#fff', zIndex: 2, height: '100%' }}
            open={loading}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        ) : (
          <Card>
            <HotTable
              data={tableData}
              colHeaders={colHeaders}
              columns={columns}
              minSpareRows={35}
              rowHeights={35}
              height="auto"
              stretchH="all"
              licenseKey="non-commercial-and-evaluation"
              rowHeaders={false}
              afterChange={(changes, source) => {
                if (changes && source !== 'loadData') {
                  const newData = [...tableData];
                  changes.forEach(([row, prop, oldVal, newVal]) => {
                    newData[row][prop] = newVal;
                  });
                  setTableData(newData);
                }
              }}
            />
          </Card>
        )}
      </Stack>
      {/* Gizli file input */}
      <input
        type="file"
        accept=".xlsx, .xls"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileUpload}
      />
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>İşlem Sonucu</DialogTitle>
        <DialogContent dividers>
          {updateResult.success.length > 0 && (
            <>
              <Typography sx={{ fontWeight: 'bold', color: 'green' }} variant="body1" gutterBottom>
                Başarıyla güncellenen kayıtlar:
              </Typography>
              {updateResult.success.map((row, idx) => (
                <Typography key={`success-${idx}`} variant="body2">
                  <b>{row.applicationCode}</b> - {row.status}
                </Typography>
              ))}
            </>
          )}
          {updateResult.errors.length > 0 && (
            <>
              <Typography sx={{ marginTop: '15px', fontWeight: 'bold', color: 'red' }} variant="body1" gutterBottom>
                Güncellenemeyen kayıtlar:
              </Typography>
              {updateResult.errors.map((row, idx) => (
                <Typography key={`error-${idx}`} variant="body2">
                  <b>{row.applicationCode || 'Boş'}</b> - {row.errorReason}
                </Typography>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)} color="primary">
            Kapat
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
